import { useState } from "react";
import { motion } from "framer-motion";
import { Check, MessageCircle, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MESSAGE = 500;

const maskPhone = (value) => {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const isSocialUrl = (value) =>
  /^(https?:\/\/)?(www\.)?(instagram\.com|linkedin\.com)\/\S+$/i.test(value.trim());

export default function QuoteModal({ prefill, close }) {
  const [form, setForm] = useState({ name: "", phone: "", social_media: "", message: prefill || "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isSocialUrl(form.social_media)) {
      setError("Informe um link válido do Instagram ou do LinkedIn (ex.: instagram.com/seuperfil).");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 10) {
      setError("Informe um telefone válido com DDD.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail?.[0]?.msg || "erro");
      setResult(data);
    } catch {
      setError("Não foi possível enviar agora. Tente novamente ou chame no WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div className="modal-backdrop" onMouseDown={close}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
      <motion.div className="quote-modal" onMouseDown={(e) => e.stopPropagation()} data-testid="quote-modal"
        initial={{ opacity: 0, y: 44, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
        <button className="modal-close" onClick={close} data-testid="close-quote-modal" aria-label="Fechar"><X /></button>
        {result ? (
          <div className="quote-success" data-testid="quote-success">
            <span className="success-ring"><Check size={26} /></span>
            <h2>Mensagem<br /><em>enviada.</em></h2>
            <p className="modal-intro">
              {result.email_sent
                ? "Vieira já recebeu a sua mensagem por e-mail e retorna em breve. Se preferir, avise também pelo WhatsApp."
                : "Sua mensagem foi registrada. Para garantir um retorno rápido, avise também pelo WhatsApp."}
            </p>
            <a className="button button-primary full" href={result.whatsapp_url} target="_blank" rel="noreferrer" data-testid="quote-whatsapp-notify">
              Avisar também no WhatsApp <MessageCircle size={17} />
            </a>
            <button className="text-button centered" onClick={close} data-testid="quote-success-close">Fechar</button>
          </div>
        ) : (
          <>
            <div className="eyebrow"><i /> PRIMEIRO PASSO</div>
            <h2>Vamos tirar sua<br /><em>ideia do papel.</em></h2>
            <p className="modal-intro">Conte quem é você e o que tem em mente. Vieira recebe a sua mensagem por e-mail e retorna com a melhor proposta.</p>
            <form onSubmit={submit} data-testid="quote-form" noValidate>
              <label>Seu nome *
                <input required value={form.name} onChange={(e) => update("name", e.target.value)}
                  placeholder="Como podemos te chamar?" data-testid="quote-name-input" maxLength={120} />
              </label>
              <label>Telefone *
                <input required value={form.phone} onChange={(e) => update("phone", maskPhone(e.target.value))}
                  placeholder="(21) 99999-9999" inputMode="tel" data-testid="quote-phone-input" />
              </label>
              <label className="span-2">Rede social (Instagram ou LinkedIn) *
                <input required value={form.social_media} onChange={(e) => update("social_media", e.target.value)}
                  placeholder="instagram.com/seuperfil" inputMode="url" data-testid="quote-social-input" maxLength={200} />
              </label>
              <label className="span-2">Sua mensagem *
                <textarea required value={form.message} onChange={(e) => update("message", e.target.value.slice(0, MAX_MESSAGE))}
                  placeholder="O que você tem em mente?" data-testid="quote-message-input" />
                <span className="char-count" data-testid="quote-message-counter">{form.message.length}/{MAX_MESSAGE}</span>
              </label>
              {error && <p className="field-error span-2" data-testid="quote-form-error">{error}</p>}
              <button className="button button-primary full span-2" disabled={sending} type="submit" data-testid="quote-submit-button">
                {sending ? "Enviando..." : "Enviar mensagem"} <Send size={17} />
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
